import { useQuery } from '@tanstack/react-query';

export type ModuleSource = 'owner' | 'workspace_override' | 'org_override' | 'plan' | 'role_restricted' | 'user_override' | 'disabled';

export interface ModulePermissions {
  view: boolean;
  edit: boolean;
  create: boolean;
  delete: boolean;
  admin: boolean;
}

export interface EffectiveModule {
  module_id: string;
  enabled: boolean;
  source: ModuleSource;
  permissions: ModulePermissions;
}

export interface UseModuleAccess {
  isOwner: boolean;
  userRole: string;
  planCode?: string;
  planName?: string;
  enabledModules: string[];
  enabledModulesCount: number;
  modules: EffectiveModule[];
  hasModule: (moduleId: string) => boolean;
  hasPermission: (moduleId: string, action: 'view' | 'edit' | 'create' | 'delete' | 'admin') => boolean;
  isLoading: boolean;
  error?: Error | null;
  allModules: EffectiveModule[];
}

export const useModuleAccess = (scope: 'workspace' | 'organization', id: string): UseModuleAccess => {
  const query = useQuery({
    queryKey: ['entitlements', scope, id],
    queryFn: async () => {
      console.log('🔍 [useModuleAccess] Fetching entitlements:', { scope, id });
      
      // Check if we're in a partner subdomain
      const hostname = window.location.hostname;
      const isPartnerSubdomain = hostname.includes('.billportal.io') && hostname !== 'uat-1.billportal.io' && hostname !== 'billportal.io';
      
      // Partner-specific module restrictions (limited access)
      const partnerModules = [
        'bill_collection', 'bill_processing', 'onboarding', 'ai_parser', 'ai_agents', 'automation',
        'reporting', 'analytics', 'compliance', 'integrations', 'api', 'storage', 'support'
      ];
      
      // Full access modules for main domain
      const allOwnerModules = [
        'bill_collection', 'bill_processing', 'onboarding', 'ai_parser', 'ai_agents', 'automation',
        'reporting', 'analytics', 'enterprise_analytics', 'compliance', 'integrations', 'api',
        'storage', 'support', 'trial_management', 'workspace_admin', 'bulk_operations',
        'audit_logs', 'security_monitoring', 'role_management', 'multi_tenant'
      ];
      
      // Use partner modules if in partner subdomain, otherwise use full modules
      const allowedModules = isPartnerSubdomain ? partnerModules : allOwnerModules;
      
      console.log('🔍 [useModuleAccess] Module access:', { 
        hostname, 
        isPartnerSubdomain, 
        allowedModules: allowedModules.length,
        modules: allowedModules 
      });
      
      const fallbackData = {
        is_owner: !isPartnerSubdomain, // Only true owners on main domain
        user_role: isPartnerSubdomain ? 'partner_admin' : 'owner',
        plan: { code: isPartnerSubdomain ? 'partner' : 'enterprise', name: isPartnerSubdomain ? 'Partner' : 'Enterprise' },
        modules: allowedModules.map(moduleId => ({
          module_id: moduleId,
          enabled: true,
          source: 'owner' as ModuleSource,
          permissions: { view: true, edit: true, create: true, delete: true, admin: !isPartnerSubdomain }
        })),
        enabled_modules: allowedModules,
        enabled_modules_count: allowedModules.length
      };
      
      console.log('🔍 [useModuleAccess] Success:', {
        plan: fallbackData.plan?.code,
        moduleCount: fallbackData.modules?.length,
        enabledCount: fallbackData.enabled_modules_count
      });
      
      return fallbackData;
    },
    enabled: !!id,
    retry: 0,
    staleTime: 60 * 1000, // 1 minute for faster updates
  });

  const hasModule = (moduleId: string): boolean => {
    // CRITICAL: Owner bypass first
    if (query.data?.is_owner) return true;
    
    // Fallback: generate enabled_modules from modules data if not provided
    const enabledModules = query.data?.enabled_modules || 
      query.data?.modules?.filter(m => m.enabled).map(m => m.module_id) || [];
    
    return enabledModules.includes(moduleId);
  };

  const hasPermission = (moduleId: string, action: 'view' | 'edit' | 'create' | 'delete' | 'admin'): boolean => {
    // CRITICAL: Owner bypass first
    if (query.data?.is_owner) return true;
    
    const module = query.data?.modules?.find(m => m.module_id === moduleId && m.enabled);
    return module?.permissions?.[action] || false;
  };

  const effectiveModules = query.data?.modules || [];
  
  // Generate enabled_modules array from modules if not provided directly
  const enabledModules = query.data?.enabled_modules || 
    effectiveModules.filter(m => m.enabled).map(m => m.module_id);

  console.log('🔍 [useModuleAccess] Debug:', {
    scope,
    id,
    isOwner: query.data?.is_owner,
    userRole: query.data?.user_role,
    planCode: query.data?.plan?.code,
    enabledCount: enabledModules.length,
    enabledModules: enabledModules,
    allModules: query.data?.modules?.map(m => ({ id: m.module_id, enabled: m.enabled })),
    rawData: query.data
  });

  return {
    isOwner: query.data?.is_owner || false,
    userRole: query.data?.user_role || 'viewer',
    planCode: query.data?.plan?.code,
    planName: query.data?.plan?.name,
    enabledModules,
    enabledModulesCount: query.data?.enabled_modules_count || 0,
    modules: effectiveModules,
    hasModule,
    hasPermission,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    allModules: effectiveModules,
  };
};